import type { AnalyticsConfig } from './analyticsConfig';
import { getName, getTitle } from '@/components/Constants';
import type {
  AssociationRow,
  CategoryCountItem,
  MetricVsScorePoint,
  ScoreDistributionSlice,
  TopEntityBarItem,
  YearPublicationPoint,
} from './analyticsTypes';
import { formatAssociationType } from '@/utils/formatAssociationType';

export const BRAND_NAVY = '#0a104f';
export const BRAND_PINK = '#be0080';
export const BRAND_MUTED = '#9a9faa';

export const CT_COLOR_BUCKETS = [
  { max: 0, color: '#c8ccd8', label: '0 clinical trials' },
  { max: 20, color: '#4a5378', label: '1–20 clinical trials' },
  { max: 100, color: BRAND_NAVY, label: '21–100 clinical trials' },
  { max: Infinity, color: BRAND_PINK, label: '>100 clinical trials' },
] as const;

const SCORE_DISTRIBUTION_COLORS = [
  '#dfe2ec',
  '#c5cad8',
  '#9aa3bc',
  '#6f7a9a',
  '#4a5378',
  '#2f365f',
  '#1c2248',
  BRAND_NAVY,
  '#080c3a',
  BRAND_PINK,
];

const DEFAULT_BAR_COLOR = BRAND_NAVY;
const HIGHLIGHT_BAR_COLOR = BRAND_PINK;

export function getCtColor(numCTs: number): string {
  const value = Number(numCTs) || 0;
  if (value === 0) return CT_COLOR_BUCKETS[0].color;
  if (value <= 20) return CT_COLOR_BUCKETS[1].color;
  if (value <= 100) return CT_COLOR_BUCKETS[2].color;
  return CT_COLOR_BUCKETS[3].color;
}

export function getRowFieldValue(row: AssociationRow, field: string): string | number | null | undefined {
  const value = row[field];
  if (Array.isArray(value)) return value[0] as string | number | null | undefined;
  return value;
}

export function average(values: number[]): number {
  if (!values.length) return 0;
  return values.reduce((sum, value) => sum + value, 0) / values.length;
}

export function getAssociatedCount(data: AssociationRow[], field: string, entityIdField?: string): number {
  if (!data.length) return 0;

  if (entityIdField) {
    const uniqueEntities = new Set(
      data.map(row => String(getRowFieldValue(row, entityIdField) ?? '').trim()).filter(Boolean),
    );
    if (uniqueEntities.size > 0) {
      const aggregateValue = Number(getRowFieldValue(data[0], field));
      const shouldUseDistinct =
        data.length > 1 ||
        !Number.isFinite(aggregateValue) ||
        aggregateValue <= 0 ||
        aggregateValue > uniqueEntities.size;
      if (shouldUseDistinct) return uniqueEntities.size;
    }
  }

  const fromRow = Number(getRowFieldValue(data[0], field));
  if (Number.isFinite(fromRow) && fromRow > 0) return fromRow;
  return data.length;
}

type SummaryMatchCardContext = {
  stats: { matchFilterCount: number; matchFilterLabel: string } | null;
  resultTotalCount: number | null;
};

function matchesSummaryMatchLabel(cardLabel: string, matchFilterLabel: string): boolean {
  const normalizedCard = cardLabel.toLowerCase();
  const normalizedMatch = matchFilterLabel.toLowerCase();
  if (normalizedMatch.includes('gene')) return normalizedCard.includes('gene');
  if (normalizedMatch.includes('disease')) return normalizedCard.includes('disease');
  if (normalizedMatch.includes('variant')) return normalizedCard.includes('variant');
  return false;
}

export function getSummaryCardValue(
  card: { id: string; getValue: (data: AssociationRow[], config: AnalyticsConfig) => number },
  data: AssociationRow[],
  config: AnalyticsConfig,
  summaryMatch?: SummaryMatchCardContext | null,
): number {
  if (summaryMatch?.stats && card.id === 'associated') {
    const cardLabel =
      typeof config.labels.associatedCount === 'string' ? config.labels.associatedCount : 'associated';
    if (matchesSummaryMatchLabel(cardLabel, summaryMatch.stats.matchFilterLabel)) {
      return summaryMatch.stats.matchFilterCount;
    }
    if (summaryMatch.resultTotalCount != null) {
      return summaryMatch.resultTotalCount;
    }
  }

  return card.getValue(data, config);
}

export function getActivePmidsCurrentYear(
  data: AssociationRow[],
  pmidsField: string,
  yearField?: string,
): number {
  if (!yearField) return 0;
  const currentYear = new Date().getFullYear();
  return data
    .filter(row => Number(row[yearField]) >= currentYear)
    .reduce((sum, row) => sum + (Number(row[pmidsField]) || 0), 0);
}

function mapTopEntities(
  data: AssociationRow[],
  sortField: string,
  config: AnalyticsConfig,
  limit: number,
): TopEntityBarItem[] {
  return [...data]
    .sort(
      (a, b) => (Number(getRowFieldValue(b, sortField)) || 0) - (Number(getRowFieldValue(a, sortField)) || 0),
    )
    .slice(0, limit)
    .map((row, index) => {
      const numCTs = config.fields.ct ? Number(getRowFieldValue(row, config.fields.ct)) || 0 : 0;
      const entityId = String(getRowFieldValue(row, config.fields.entityId) ?? '');
      return {
        entityId,
        normalizedScore: Number(getRowFieldValue(row, config.fields.score)) || 0,
        numPMIDs: Number(getRowFieldValue(row, config.fields.papers)) || 0,
        numCTs,
        metricValue: Number(getRowFieldValue(row, sortField)) || 0,
        color: config.features.ctLegend
          ? getCtColor(numCTs)
          : index === 0
            ? HIGHLIGHT_BAR_COLOR
            : DEFAULT_BAR_COLOR,
      };
    });
}

export function getTopEntitiesByScore(
  data: AssociationRow[],
  config: AnalyticsConfig,
  limit = 15,
): TopEntityBarItem[] {
  return mapTopEntities(data, config.fields.score, config, limit);
}

export function getTopEntitiesByPapers(
  data: AssociationRow[],
  config: AnalyticsConfig,
  limit = 15,
): TopEntityBarItem[] {
  return mapTopEntities(data, config.fields.papers, config, limit);
}

export function getTopEntitiesByMetric(
  data: AssociationRow[],
  config: AnalyticsConfig,
  metricField: string,
  limit = 15,
): TopEntityBarItem[] {
  return mapTopEntities(data, metricField || config.fields.score, config, limit);
}

export function getCumulativePublicationsByYear(
  data: AssociationRow[],
  yearField: string,
  pmidsField: string,
): YearPublicationPoint[] {
  const yearCounts = new Map<number, number>();

  data.forEach(row => {
    const year = Number(row[yearField]);
    const pmids = Number(row[pmidsField]) || 0;
    if (!Number.isFinite(year) || pmids <= 0) return;
    yearCounts.set(year, (yearCounts.get(year) || 0) + pmids);
  });

  const years = [...yearCounts.keys()].sort((a, b) => a - b);
  if (!years.length) return [];

  const minYear = Math.min(...years);
  const maxYear = Math.max(...years);
  let cumulative = 0;
  const points: YearPublicationPoint[] = [];

  for (let year = minYear; year <= maxYear; year += 1) {
    const count = yearCounts.get(year) || 0;
    cumulative += count;
    points.push({ year, count, cumulative });
  }

  return points;
}

export function getScoreDistribution(data: AssociationRow[], scoreField: string): ScoreDistributionSlice[] {
  return getMetricDistribution(data, scoreField, { kind: 'unit' });
}

const CATEGORY_BAR_COLORS = [
  BRAND_NAVY,
  '#1c2248',
  '#2f365f',
  '#4a5378',
  '#6f7a9a',
  '#9aa3bc',
  BRAND_PINK,
  '#d43d9a',
  '#a0126a',
  BRAND_MUTED,
  '#c8ccd8',
  '#dfe2ec',
];

function normalizeCategoryToken(raw: unknown): string | null {
  if (raw == null) return null;
  const text = String(raw).trim();
  if (!text || text === '-' || text.toLowerCase() === 'null' || text.toLowerCase() === 'undefined') {
    return null;
  }
  return text;
}

function splitCategoryValues(value: AssociationRow[string]): string[] {
  if (Array.isArray(value)) {
    return value.map(normalizeCategoryToken).filter((entry): entry is string => Boolean(entry));
  }
  const single = normalizeCategoryToken(value);
  if (!single) return [];
  if (single.includes('|')) {
    return single
      .split('|')
      .map(normalizeCategoryToken)
      .filter((entry): entry is string => Boolean(entry));
  }
  if (single.includes(';')) {
    return single
      .split(';')
      .map(normalizeCategoryToken)
      .filter((entry): entry is string => Boolean(entry));
  }
  return [single];
}

function formatCategoryLabel(field: string, raw: string): string {
  if (field === 'associationType') return formatAssociationType(raw);
  return raw;
}

export function getCategoryCounts(
  data: AssociationRow[],
  field: string,
  options: { limit?: number } = {},
): CategoryCountItem[] {
  const limit = options.limit ?? 12;
  const counts = new Map<string, number>();

  data.forEach(row => {
    const tokens = splitCategoryValues(row[field]);
    if (!tokens.length) {
      counts.set('__missing__', (counts.get('__missing__') ?? 0) + 1);
      return;
    }
    const unique = new Set(tokens);
    unique.forEach(token => {
      counts.set(token, (counts.get(token) ?? 0) + 1);
    });
  });

  const sorted = [...counts.entries()]
    .filter(([key]) => key !== '__missing__')
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));

  const missing = counts.get('__missing__') ?? 0;
  const head = sorted.slice(0, limit);
  const tail = sorted.slice(limit);
  const otherCount = tail.reduce((sum, [, count]) => sum + count, 0) + missing;

  const items: CategoryCountItem[] = head.map(([key, value], index) => ({
    id: key,
    label: formatCategoryLabel(field, key),
    value,
    color: CATEGORY_BAR_COLORS[index % CATEGORY_BAR_COLORS.length],
  }));

  if (otherCount > 0) {
    items.push({
      id: '__other__',
      label: 'Other / missing',
      value: otherCount,
      color: BRAND_MUTED,
    });
  }

  return items;
}

const YEAR_BUCKETS = [
  { id: 'lt1990', label: '<1990', min: Number.NEGATIVE_INFINITY, max: 1989 },
  { id: '1990s', label: '1990s', min: 1990, max: 1999 },
  { id: '2000s', label: '2000s', min: 2000, max: 2009 },
  { id: '2010s', label: '2010s', min: 2010, max: 2019 },
  { id: '2020plus', label: '2020+', min: 2020, max: Number.POSITIVE_INFINITY },
] as const;

export function getYearBucketCounts(data: AssociationRow[], field: string): CategoryCountItem[] {
  const counts = YEAR_BUCKETS.map(() => 0);

  data.forEach(row => {
    const year = Number(getRowFieldValue(row, field));
    if (!Number.isFinite(year)) return;
    const index = YEAR_BUCKETS.findIndex(bucket => year >= bucket.min && year <= bucket.max);
    if (index >= 0) counts[index] += 1;
  });

  return YEAR_BUCKETS.map((bucket, index) => ({
    id: bucket.id,
    label: bucket.label,
    value: counts[index],
    color: CATEGORY_BAR_COLORS[index % CATEGORY_BAR_COLORS.length],
  }));
}

type DistributionDomain =
  | { kind: 'unit' }
  | { kind: 'nonNegative' }
  | { kind: 'bounded'; min: number; max: number }
  | { kind: 'auto' };

function buildEqualWidthBuckets(min: number, max: number, count = 10) {
  if (!Number.isFinite(min) || !Number.isFinite(max) || max <= min) {
    return Array.from({ length: count }, (_, index) => ({
      min: index * 0.1,
      max: (index + 1) * 0.1,
      count: 0,
    }));
  }

  const span = max - min;
  const bucketSize = span / count;
  return Array.from({ length: count }, (_, index) => ({
    min: min + index * bucketSize,
    max: index === count - 1 ? max : min + (index + 1) * bucketSize,
    count: 0,
  }));
}

export function getMetricDistribution(
  data: AssociationRow[],
  field: string,
  domain: DistributionDomain = { kind: 'unit' },
): ScoreDistributionSlice[] {
  const values = data.map(row => Number(getRowFieldValue(row, field))).filter(Number.isFinite) as number[];

  let buckets: Array<{ min: number; max: number; count: number }>;

  if (domain.kind === 'unit') {
    const bucketSize = 0.1;
    buckets = Array.from({ length: 10 }, (_, index) => ({
      min: index * bucketSize,
      max: (index + 1) * bucketSize,
      count: 0,
    }));
    values.forEach(score => {
      const clamped = Math.min(Math.max(score, 0), 0.999999);
      const bucketIndex = Math.min(Math.floor(clamped / bucketSize), buckets.length - 1);
      buckets[bucketIndex].count += 1;
    });
  } else if (domain.kind === 'bounded') {
    buckets = buildEqualWidthBuckets(domain.min, domain.max);
    values.forEach(score => {
      const clamped = Math.min(Math.max(score, domain.min), domain.max - Number.EPSILON);
      const span = domain.max - domain.min || 1;
      const bucketIndex = Math.min(
        Math.floor(((clamped - domain.min) / span) * buckets.length),
        buckets.length - 1,
      );
      buckets[bucketIndex].count += 1;
    });
  } else {
    const min = values.length ? Math.min(...values) : 0;
    const max = values.length ? Math.max(...values) : 1;
    buckets = buildEqualWidthBuckets(min, max === min ? min + 1 : max);
    values.forEach(score => {
      const span = buckets[buckets.length - 1].max - buckets[0].min || 1;
      const bucketIndex = Math.min(
        Math.floor(((score - buckets[0].min) / span) * buckets.length),
        buckets.length - 1,
      );
      buckets[Math.max(0, bucketIndex)].count += 1;
    });
  }

  const total = data.length || 1;
  const span = buckets[buckets.length - 1].max - buckets[0].min;
  const decimals = domain.kind === 'unit' ? 1 : span >= 10 ? 0 : 2;

  return buckets.map((bucket, index) => {
    const minLabel = bucket.min.toFixed(decimals);
    const maxLabel = bucket.max.toFixed(decimals);
    const percentage = Math.round((bucket.count / total) * 100);
    const isLastBucket = index === buckets.length - 1;

    return {
      id: `${minLabel}-${maxLabel}`,
      label: `${minLabel}–${maxLabel} (${percentage}%)`,
      value: bucket.count,
      color: SCORE_DISTRIBUTION_COLORS[index] ?? SCORE_DISTRIBUTION_COLORS.at(-1)!,
      minScore: bucket.min,
      maxScore: bucket.max,
      isLastBucket,
    };
  });
}

export function getMetricVsScoreData(
  data: AssociationRow[],
  config: AnalyticsConfig,
  metricKey: string,
  limit = 50,
): MetricVsScorePoint[] {
  return getMetricScatterData(data, config, metricKey, config.fields.score, limit);
}

export function getMetricScatterData(
  data: AssociationRow[],
  config: AnalyticsConfig,
  xField: string,
  yField: string,
  limit = 50,
): MetricVsScorePoint[] {
  const sortField = yField || config.fields.score;
  return [...data]
    .sort(
      (a, b) => (Number(getRowFieldValue(b, sortField)) || 0) - (Number(getRowFieldValue(a, sortField)) || 0),
    )
    .slice(0, limit)
    .map(row => ({
      id: String(getRowFieldValue(row, config.fields.entityId) ?? ''),
      entityId: String(getRowFieldValue(row, config.fields.entityId) ?? ''),
      x: Number(getRowFieldValue(row, xField)) || 0,
      y: Number(getRowFieldValue(row, yField)) || 0,
    }));
}

export function averageField(data: AssociationRow[], field: string): number {
  return average(data.map(row => Number(getRowFieldValue(row, field))).filter(Number.isFinite));
}

export function sumField(data: AssociationRow[], field: string): number {
  return data
    .map(row => Number(getRowFieldValue(row, field)))
    .filter(Number.isFinite)
    .reduce((sum, value) => sum + value, 0);
}

export function maxField(data: AssociationRow[], field: string): number {
  const values = data.map(row => Number(getRowFieldValue(row, field))).filter(Number.isFinite);
  return values.length ? Math.max(...values) : 0;
}

export function minField(data: AssociationRow[], field: string): number {
  const values = data.map(row => Number(getRowFieldValue(row, field))).filter(Number.isFinite);
  return values.length ? Math.min(...values) : 0;
}

export function pickRandomScatterLabelIds(entityIds: string[], count = 10): Set<string> {
  const uniqueIds = [...new Set(entityIds.filter(Boolean))];
  if (uniqueIds.length <= count) return new Set(uniqueIds);

  const shuffled = [...uniqueIds];
  for (let index = shuffled.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(Math.random() * (index + 1));
    [shuffled[index], shuffled[swapIndex]] = [shuffled[swapIndex], shuffled[index]];
  }

  return new Set(shuffled.slice(0, count));
}

const LABEL_ELLIPSIS = '...';

export const BAR_CHART_AXIS_LABEL_MAX_LENGTH = 15;

export function truncateChartLabel(label: string, maxLength = 24): string {
  if (label.length <= maxLength) return label;
  return `${label.slice(0, maxLength - LABEL_ELLIPSIS.length)}${LABEL_ELLIPSIS}`;
}

export function truncateScatterLabel(label: string, maxLength = 24): string {
  return truncateChartLabel(label, maxLength);
}

export function formatMetricNumber(value: number, decimals = 0): string {
  if (!Number.isFinite(value)) return '—';
  if (decimals > 0) return value.toFixed(decimals);
  return Math.round(value).toLocaleString('es-ES');
}

export function buildSummaryValues(data: AssociationRow[], config: AnalyticsConfig): Record<string, number> {
  return Object.fromEntries(config.summaryCards.map(card => [card.id, card.getValue(data, config)]));
}

export function getSummaryCardLabel(
  card: AnalyticsConfig['summaryCards'][number],
  currentYear: number,
): string {
  return typeof card.label === 'function' ? card.label(currentYear) : card.label;
}

function toSearchItemLabel(item: {
  id?: unknown;
  name?: unknown;
  label?: unknown;
  [key: string]: unknown;
}): string {
  const title = getTitle(item);
  const id = getName(item);

  if (typeof title === 'string' && title) return title;
  if (typeof title === 'number' || typeof title === 'boolean') return String(title);
  if (typeof title === 'object' && title !== null && 'title' in title) {
    return String((title as { title?: unknown }).title ?? id ?? item.id ?? 'Item');
  }

  const fallback = id || item.id;
  return fallback != null && fallback !== '' ? String(fallback) : 'Item';
}

export function buildAnalyticsSearchTitle(
  names: Array<{ id?: unknown; name?: unknown; label?: unknown; [key: string]: unknown }>,
): string {
  if (!names?.length) return 'Search';

  if (names.length === 1) return toSearchItemLabel(names[0]);

  const preview = names.slice(0, 3).map(toSearchItemLabel).join(', ');
  return names.length > 3 ? `${preview} +${names.length - 3} more` : preview;
}

export { getTopEntitiesByScore as getTopGenesByScore };
export { getTopEntitiesByPapers as getTopGenesByPapers };
export type { AssociationRow as GdaAssociationRow };
